import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listSchools,
  upsertSchool,
  deleteSchool,
  setSchoolApproved,
  uploadSchoolLogo,
  uploadSchoolMockup,
  removeSchoolMockup,
  mockupKey,
  parseSchoolsCsv,
  importSchools,
  LOGO_STATUS,
} from "../../services/schoolsService";
import { listProducts, updateProduct } from "../productCatalog/productsService";
import "./admin-schools.css";

const BLANK = {
  name: "",
  state: "",
  district: "",
  mascot: "",
  logoUrl: "",
  approved: false,
  licenseNote: "",
};

export default function AdminSchools() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("schools");
  const [schools, setSchools] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [mockupSchool, setMockupSchool] = useState(null);
  const [overlayProduct, setOverlayProduct] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([listSchools(), listProducts()]);
      setSchools(s);
      setProducts(p);
    } catch (err) {
      console.error("Load failed:", err);
      alert(`Could not load: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const states = useMemo(
    () => [...new Set(schools.map((s) => s.state).filter(Boolean))].sort(),
    [schools]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schools
      .filter((s) => stateFilter === "all" || s.state === stateFilter)
      .filter(
        (s) =>
          !q ||
          [s.name, s.district, s.mascot, s.state]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(q)
      );
  }, [schools, search, stateFilter]);

  const counts = useMemo(
    () => ({
      total: schools.length,
      approved: schools.filter((s) => s.approved).length,
      needLogo: schools.filter((s) => s.logoStatus === LOGO_STATUS.PENDING).length,
      schoolProducts: products.filter((p) => p.requiresSchool === true).length,
    }),
    [schools, products]
  );

  const handleSave = async () => {
    if (!editing?.name || !editing?.state) {
      alert("School name and state are required.");
      return;
    }
    setBusy("Saving…");
    try {
      await upsertSchool(editing);
      setEditing(null);
      await load();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handleLogo = async (school, file) => {
    if (!file) return;
    setBusy(`Uploading logo for ${school.name}…`);
    try {
      await uploadSchoolLogo(school.id, file);
      await load();
    } catch (err) {
      alert(`Logo upload failed: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handleApprove = async (school) => {
    if (
      !school.approved &&
      !window.confirm(
        `Approve ${school.name} for merchandise sales?\n\n` +
          "Only approve once you've confirmed the district permits use of the " +
          "school name, mascot and logo. Approved schools appear in the " +
          "storefront dropdown immediately."
      )
    )
      return;

    setBusy("Updating…");
    try {
      await setSchoolApproved(school.id, !school.approved);
      await load();
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async (school) => {
    if (!window.confirm(`Delete ${school.name}? This cannot be undone.`)) return;
    setBusy("Deleting…");
    try {
      await deleteSchool(school.id);
      await load();
    } finally {
      setBusy("");
    }
  };

  const handleMockupUpload = async (school, product, colorKey, file) => {
    if (!file) return;
    setBusy(`Uploading mockup…`);
    try {
      await uploadSchoolMockup(school.id, product.id, colorKey, file);
      const fresh = await listSchools();
      setSchools(fresh);
      setMockupSchool(fresh.find((s2) => s2.id === school.id) || null);
    } catch (err) {
      alert(`Mockup upload failed: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handleMockupRemove = async (school, product, colorKey) => {
    setBusy("Removing mockup…");
    try {
      await removeSchoolMockup(school.id, product.id, colorKey);
      const fresh = await listSchools();
      setSchools(fresh);
      setMockupSchool(fresh.find((s2) => s2.id === school.id) || null);
    } finally {
      setBusy("");
    }
  };

  const handleSaveOverlay = async () => {
    if (!overlayProduct) return;
    setBusy("Saving overlay…");
    try {
      await updateProduct(overlayProduct.id, {
        logoOverlay: overlayProduct.logoOverlay,
      });
      setOverlayProduct(null);
      await load();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handlePreviewImport = () => setImportPreview(parseSchoolsCsv(importText));

  const handleRunImport = async () => {
    if (importPreview.length === 0) return;
    setBusy(`Importing ${importPreview.length} schools…`);
    try {
      const res = await importSchools(importPreview);
      alert(
        `Imported ${res.written} schools.\n\n` +
          "They are NOT approved yet — approve each one after confirming " +
          "district permission."
      );
      setImportText("");
      setImportPreview([]);
      setShowImport(false);
      await load();
    } catch (err) {
      alert(`Import failed: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const toggleRequiresSchool = async (product) => {
    setBusy("Updating product…");
    try {
      await updateProduct(product.id, {
        requiresSchool: product.requiresSchool !== true,
      });
      await load();
    } catch (err) {
      alert(`Update failed: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="as-page">
      <div className="as-topbar">
        <button className="as-btn as-btn-dark" onClick={() => navigate("/admin")}>
          ← Admin Dashboard
        </button>
        <h1 className="as-title">Schools &amp; Personalized Products</h1>
        <button className="as-btn" onClick={load} disabled={!!busy}>
          Refresh
        </button>
      </div>

      {busy && <div className="as-busy">{busy}</div>}

      <div className="as-stats">
        <div className="as-stat">
          <span className="as-stat-num">{counts.total}</span>
          <span className="as-stat-label">Schools</span>
        </div>
        <div className="as-stat">
          <span className="as-stat-num">{counts.approved}</span>
          <span className="as-stat-label">Approved for sale</span>
        </div>
        <div className="as-stat">
          <span className="as-stat-num">{counts.needLogo}</span>
          <span className="as-stat-label">Awaiting logo</span>
        </div>
        <div className="as-stat">
          <span className="as-stat-num">{counts.schoolProducts}</span>
          <span className="as-stat-label">School products</span>
        </div>
      </div>

      <div className="as-tabs">
        <button
          className={`as-tab ${tab === "schools" ? "is-active" : ""}`}
          onClick={() => setTab("schools")}
        >
          Schools
        </button>
        <button
          className={`as-tab ${tab === "products" ? "is-active" : ""}`}
          onClick={() => setTab("products")}
        >
          Personalized Products
        </button>
      </div>

      {tab === "schools" && (
        <>
          <div className="as-toolbar">
            <input
              className="as-search"
              placeholder="Search school, district, mascot…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="as-select"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="all">All states</option>
              {states.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              className="as-btn as-btn-green"
              onClick={() => setEditing({ ...BLANK })}
            >
              + Add school
            </button>
            <button className="as-btn as-btn-blue" onClick={() => setShowImport(true)}>
              Bulk import
            </button>
          </div>

          <div className="as-table-wrap">
            {loading ? (
              <p className="as-empty">Loading…</p>
            ) : (
              <table className="as-table">
                <thead>
                  <tr>
                    <th>Logo</th>
                    <th>School</th>
                    <th>State</th>
                    <th>District</th>
                    <th>Mascot</th>
                    <th>Artwork</th>
                    <th>Licensed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="as-empty">
                        No schools yet. Add one or bulk import a CSV.
                      </td>
                    </tr>
                  ) : (
                    visible.map((s) => (
                      <tr key={s.id}>
                        <td>
                          {s.logoUrl ? (
                            <img className="as-logo" src={s.logoUrl} alt="" />
                          ) : (
                            <div className="as-logo as-logo-empty">?</div>
                          )}
                        </td>
                        <td>
                          <strong>{s.name}</strong>
                        </td>
                        <td>{s.state}</td>
                        <td>{s.district || "—"}</td>
                        <td>{s.mascot || "—"}</td>
                        <td>
                          {s.logoStatus === LOGO_STATUS.ON_FILE ? (
                            <span className="as-badge as-badge-green">On file</span>
                          ) : (
                            <span className="as-badge as-badge-amber">Needed</span>
                          )}
                        </td>
                        <td>
                          {s.approved ? (
                            <span className="as-badge as-badge-green">Approved</span>
                          ) : (
                            <span className="as-badge as-badge-grey">Not for sale</span>
                          )}
                        </td>
                        <td className="as-actions">
                          <button className="as-mini as-mini-blue" onClick={() => setEditing(s)}>
                            Edit
                          </button>

                          <label className="as-mini as-mini-teal as-upload">
                            Logo
                            <input
                              type="file"
                              accept="image/*,.svg"
                              onChange={(e) => handleLogo(s, e.target.files?.[0])}
                            />
                          </label>

                          <button
                            className="as-mini as-mini-purple"
                            onClick={() => setMockupSchool(s)}
                            title="Upload finished product photos for this school"
                          >
                            Mockups
                            {s.productImages
                              ? ` (${Object.keys(s.productImages).length})`
                              : ""}
                          </button>

                          <button
                            className={`as-mini ${s.approved ? "as-mini-amber" : "as-mini-green"}`}
                            onClick={() => handleApprove(s)}
                          >
                            {s.approved ? "Unapprove" : "Approve"}
                          </button>

                          <button className="as-mini as-mini-red" onClick={() => handleDelete(s)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === "products" && (
        <div className="as-table-wrap">
          <p className="as-note">
            Switch on any product that gets personalized with a school. Shoppers
            then pick a school on the product card, and a signed-in buyer sees
            their own school pre-selected. Prices and sizes stay exactly as they
            are — the school is not a separate product.
            <br />
            <br />
            <strong>Product photos:</strong> upload a finished mockup per school
            under Schools ▸ Mockups and it replaces the plain photo the moment
            that school is picked. Where no mockup exists, turn on "Logo
            position" and the school's logo is composited over the plain photo
            automatically — so you only need to shoot the combinations that
            matter most.
          </p>

          <table className="as-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Sizes</th>
                <th>Personalized</th>
                <th>Logo preview</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="as-empty">
                    No products in the catalog yet.
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                    </td>
                    <td>{p.categoryId || "—"}</td>
                    <td>{p.hasSizes ? (p.selectedSizes || []).join(", ") || "yes" : "—"}</td>
                    <td>
                      {p.requiresSchool ? (
                        <span className="as-badge as-badge-green">School required</span>
                      ) : (
                        <span className="as-badge as-badge-grey">Standard</span>
                      )}
                    </td>
                    <td>
                      {p.logoOverlay?.enabled ? (
                        <span className="as-badge as-badge-green">On</span>
                      ) : (
                        <span className="as-badge as-badge-grey">Off</span>
                      )}
                    </td>
                    <td className="as-actions">
                      <button
                        className={`as-mini ${p.requiresSchool ? "as-mini-amber" : "as-mini-green"}`}
                        onClick={() => toggleRequiresSchool(p)}
                      >
                        {p.requiresSchool ? "Make standard" : "Make personalized"}
                      </button>
                      {p.requiresSchool && (
                        <button
                          className="as-mini as-mini-blue"
                          onClick={() =>
                            setOverlayProduct({
                              ...p,
                              logoOverlay: {
                                enabled: p.logoOverlay?.enabled ?? false,
                                top: p.logoOverlay?.top ?? 32,
                                left: p.logoOverlay?.left ?? 50,
                                width: p.logoOverlay?.width ?? 30,
                              },
                            })
                          }
                        >
                          Logo position
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="as-modal-overlay" onClick={() => setEditing(null)}>
          <div className="as-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing.id ? `Edit ${editing.name}` : "Add school"}</h2>

            <div className="as-form-grid">
              <label>
                School name *
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </label>
              <label>
                State *
                <input
                  maxLength={2}
                  value={editing.state}
                  onChange={(e) =>
                    setEditing({ ...editing, state: e.target.value.toUpperCase() })
                  }
                />
              </label>
              <label>
                District
                <input
                  value={editing.district}
                  onChange={(e) => setEditing({ ...editing, district: e.target.value })}
                />
              </label>
              <label>
                Mascot
                <input
                  value={editing.mascot}
                  onChange={(e) => setEditing({ ...editing, mascot: e.target.value })}
                />
              </label>
            </div>

            <label className="as-full">
              Licensing note
              <textarea
                rows={3}
                placeholder="Who approved use of the name/logo, and when."
                value={editing.licenseNote}
                onChange={(e) => setEditing({ ...editing, licenseNote: e.target.value })}
              />
            </label>

            <label className="as-check">
              <input
                type="checkbox"
                checked={editing.approved === true}
                onChange={(e) => setEditing({ ...editing, approved: e.target.checked })}
              />
              Approved for merchandise sales
            </label>

            <div className="as-modal-actions">
              <button className="as-btn as-btn-green" onClick={handleSave}>
                Save
              </button>
              <button className="as-btn" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {mockupSchool && (
        <div className="as-modal-overlay" onClick={() => setMockupSchool(null)}>
          <div className="as-modal as-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Mockups — {mockupSchool.name}</h2>
            <p className="as-modal-sub">
              Upload the finished product photo with this school's artwork
              already on it. One per colour gives the best result; a single
              "Any colour" image is used as a fallback for colours you haven't
              shot. Anything left empty falls back to the plain product photo,
              so partial coverage is fine.
            </p>

            {products.filter((p) => p.requiresSchool).length === 0 ? (
              <p className="as-empty">
                No personalized products yet — flag one under the Personalized
                Products tab first.
              </p>
            ) : (
              products
                .filter((p) => p.requiresSchool)
                .map((product) => {
                  // A product with no colours already uses colorKey
                  // "default" (see AddProductForm), which is the same key as
                  // the fallback slot — so dedupe, or that product shows the
                  // same slot twice writing over itself.
                  const variantSlots = (product.variants || []).map((v) => ({
                    colorKey: v.colorKey || "default",
                    label: v.label || v.colorKey || "Default",
                    swatch: v.swatch,
                  }));

                  const hasDefaultVariant = variantSlots.some(
                    (v) => v.colorKey === "default"
                  );

                  const colorSlots = hasDefaultVariant
                    ? variantSlots
                    : [
                        { colorKey: "default", label: "Any colour (fallback)" },
                        ...variantSlots,
                      ];

                  return (
                    <div key={product.id} className="as-mockup-product">
                      <h3>{product.name}</h3>
                      <div className="as-mockup-grid">
                        {colorSlots.map((slot) => {
                          const url =
                            mockupSchool.productImages?.[
                              mockupKey(product.id, slot.colorKey)
                            ];

                          return (
                            <div key={slot.colorKey} className="as-mockup-slot">
                              <div className="as-mockup-thumb">
                                {url ? (
                                  <img src={url} alt={slot.label} />
                                ) : (
                                  <span className="as-mockup-empty">
                                    no image
                                  </span>
                                )}
                              </div>

                              <div className="as-mockup-label">
                                {slot.swatch && (
                                  <span
                                    className="as-swatch-dot"
                                    style={{ backgroundColor: slot.swatch }}
                                  />
                                )}
                                {slot.label}
                              </div>

                              <label className="as-mini as-mini-teal as-upload">
                                {url ? "Replace" : "Upload"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) =>
                                    handleMockupUpload(
                                      mockupSchool,
                                      product,
                                      slot.colorKey,
                                      e.target.files?.[0]
                                    )
                                  }
                                />
                              </label>

                              {url && (
                                <button
                                  className="as-mini as-mini-red"
                                  onClick={() =>
                                    handleMockupRemove(
                                      mockupSchool,
                                      product,
                                      slot.colorKey
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
            )}

            <div className="as-modal-actions">
              <button className="as-btn" onClick={() => setMockupSchool(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {overlayProduct && (
        <div className="as-modal-overlay" onClick={() => setOverlayProduct(null)}>
          <div className="as-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Logo position — {overlayProduct.name}</h2>
            <p className="as-modal-sub">
              Where the school logo sits when no mockup has been uploaded. The
              preview uses the product's own photo, so what you see is what
              shoppers get. A real mockup always wins over this.
            </p>

            <label className="as-check">
              <input
                type="checkbox"
                checked={overlayProduct.logoOverlay.enabled}
                onChange={(e) =>
                  setOverlayProduct({
                    ...overlayProduct,
                    logoOverlay: {
                      ...overlayProduct.logoOverlay,
                      enabled: e.target.checked,
                    },
                  })
                }
              />
              Show the school logo on the plain product photo
            </label>

            <div className="as-overlay-preview">
              {(overlayProduct.image || overlayProduct.variants?.[0]?.image) && (
                <img
                  className="as-overlay-base"
                  src={overlayProduct.image || overlayProduct.variants[0].image}
                  alt=""
                />
              )}
              {overlayProduct.logoOverlay.enabled && (
                <div
                  className="as-overlay-marker"
                  style={{
                    top: `${overlayProduct.logoOverlay.top}%`,
                    left: `${overlayProduct.logoOverlay.left}%`,
                    width: `${overlayProduct.logoOverlay.width}%`,
                  }}
                >
                  LOGO
                </div>
              )}
            </div>

            {["top", "left", "width"].map((field) => (
              <label key={field} className="as-slider-row">
                <span>
                  {field} — {overlayProduct.logoOverlay[field]}%
                </span>
                <input
                  type="range"
                  min={field === "width" ? 5 : 0}
                  max={field === "width" ? 80 : 100}
                  value={overlayProduct.logoOverlay[field]}
                  onChange={(e) =>
                    setOverlayProduct({
                      ...overlayProduct,
                      logoOverlay: {
                        ...overlayProduct.logoOverlay,
                        [field]: Number(e.target.value),
                      },
                    })
                  }
                />
              </label>
            ))}

            <div className="as-modal-actions">
              <button className="as-btn as-btn-green" onClick={handleSaveOverlay}>
                Save
              </button>
              <button className="as-btn" onClick={() => setOverlayProduct(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <div className="as-modal-overlay" onClick={() => setShowImport(false)}>
          <div className="as-modal as-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Bulk import schools</h2>
            <p className="as-modal-sub">
              CSV with a header row. Columns: name, state, district, mascot,
              logoUrl, approved. Imported schools are unapproved by default.
            </p>

            <textarea
              className="as-import-box"
              rows={10}
              placeholder={"name,state,district,mascot\nPark Hill Elementary,VA,Fairfax County,Panthers"}
              value={importText}
              onChange={(e) => {
                setImportText(e.target.value);
                setImportPreview([]);
              }}
            />

            <div className="as-modal-actions">
              <button className="as-btn as-btn-blue" onClick={handlePreviewImport}>
                Preview
              </button>
              <button
                className="as-btn as-btn-green"
                onClick={handleRunImport}
                disabled={importPreview.length === 0}
              >
                Import {importPreview.length || ""}
              </button>
              <button className="as-btn" onClick={() => setShowImport(false)}>
                Cancel
              </button>
            </div>

            {importPreview.length > 0 && (
              <div className="as-preview">
                <strong>{importPreview.length} schools parsed</strong>
                <ul>
                  {importPreview.slice(0, 40).map((r, i) => (
                    <li key={i}>
                      {r.state} — {r.name}
                      {r.district ? ` (${r.district})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
